import { useCallback, useSyncExternalStore } from 'react';

/**
 * Age under which data reads « À jour » / "Up to date" (M7 R9). Proposal, to be validated (Q60):
 * three missed polls of 5 s.
 */
export const STALE_AFTER_MS = 15_000;

/**
 * Margin added to the staleness timer, so it fires after the instant and the check sees the data
 * stale (timers may fire a little early in browsers).
 */
const TIMER_MARGIN_MS = 1;

/**
 * Whether data received at `receivedAt` (device clock, `Date.now()`: `receivedAt` of
 * `useApiQuery`) is younger than `staleAfterMs` (M7 R9). Re-renders once, at the instant the data
 * turns stale, instead of ticking every second.
 *
 * Judged on the receipt time, not on the server's `generatedAt` / `refreshedAt`: both sides of the
 * comparison then come from the same clock, so a phone clock minutes ahead or behind the server
 * never turns the indicator wrong (proposal, to be validated, sub-question of Q60). The screen
 * still displays the server's time.
 *
 * A `receivedAt` that is not a number (NaN) counts as stale: data is never shown as fresh by
 * mistake (R8). So does a `receivedAt` in the future (the device clock was set back after the
 * data arrived): its age is unknown, and waiting for the clock to catch up could show it fresh
 * for hours.
 */
export function useIsFresh(receivedAt: number, staleAfterMs: number = STALE_AFTER_MS): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const freshNow = (): boolean => isFresh(Date.now() - receivedAt, staleAfterMs);
      const arm = (): void => {
        // Already stale, unreadable (NaN) or received "in the future" (clock set back): nothing
        // will change until new data arrives.
        if (!freshNow()) return;
        timer = setTimeout(
          () => {
            // Re-check rather than trust the timer: it may fire early (device clock set back a
            // little, throttled background timers). Still fresh: wait again.
            if (freshNow()) arm();
            else onChange();
          },
          receivedAt + staleAfterMs - Date.now() + TIMER_MARGIN_MS,
        );
      };
      arm();
      return () => {
        clearTimeout(timer);
      };
    },
    [receivedAt, staleAfterMs],
  );
  // A boolean snapshot stays equal between calls, so React re-renders only when it flips.
  return useSyncExternalStore(subscribe, () => isFresh(Date.now() - receivedAt, staleAfterMs));
}

/** Fresh only for an age in [0, staleAfterMs): a negative age (clock set back) is stale. */
function isFresh(age: number, staleAfterMs: number): boolean {
  return age >= 0 && age < staleAfterMs;
}
